import React, { useCallback, useEffect, useState } from 'react';
import Home from './Home';
import ProductDetail from './ProductDetail';
import Dashboard from './Dashboard';
import AdminDashboard from './AdminDashboard';
import VendorDashboard from './VendorDashboard';
import PrivacyPolicy from './PrivacyPolicy';
import LoginForm from './LoginForm';
import RegisterForm from './RegisterForm';
import CartPage from './CartPage';
import OrderTrackingPage from './OrderTrackingPage';
import ChatWidget from './ChatWidget';
import CookieConsentBanner from './CookieConsentBanner';
import { authService, api, panierService } from '../services/api';
import { usePanier } from '../hooks/useAgroshopHooks';
import { Routes, Route, Link, useLocation, useNavigate, Navigate } from 'react-router-dom';

export default function App() {
  const [currentUser, setCurrentUser] = useState(undefined);
  const [authLoading, setAuthLoading] = useState(true);
  const { nbItems, ajouter } = usePanier(!!currentUser);
  const location = useLocation();
  const navigate = useNavigate();

  const loadProfile = useCallback(async () => {
    if (!authService.estConnecte()) {
      const refreshed = await api.init();
      if (!refreshed) {
        setCurrentUser(null);
        setAuthLoading(false);
        return null;
      }
    }

    try {
      const profile = await authService.me();
      setCurrentUser(profile);
      return profile;
    } catch {
      authService.logout();
      setCurrentUser(null);
      return null;
    } finally {
      setAuthLoading(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    const init = async () => {
      if (!mounted) return;
      // Essayer une initialisation silencieuse (récupérer access via refresh cookie)
      try {
        await api.init();
      } catch (e) {
        // ignore
      }
      await loadProfile();
    };
    init();

    const handler = (event) => {
      if (event.key === 'access_token' || event.key === 'refresh_token') {
        loadProfile();
      }
    };
    window.addEventListener('storage', handler);
    return () => {
      mounted = false;
      window.removeEventListener('storage', handler);
    };
  }, [loadProfile]);

  useEffect(() => {
    if (!currentUser) {
      return;
    }

    const syncCart = async () => {
      try {
        await panierService.syncFromServer();
      } catch {
        // Synchronisation silencieuse, ignorer les erreurs réseau
      }
    };

    syncCart();
  }, [currentUser]);

  const refreshAuth = useCallback(async () => {
    if (!authService.estConnecte()) {
      const refreshed = await api.init();
      if (!refreshed) {
        setCurrentUser(null);
        return null;
      }
    }
    try {
      const profile = await authService.me();
      setCurrentUser(profile);
      return profile;
    } catch {
      authService.logout();
      setCurrentUser(null);
      return null;
    }
  }, []);

  const handleLoginSuccess = async () => {
    const profile = await refreshAuth();
    if (profile?.role === 'admin') {
      navigate('/admin');
    } else if (profile?.role === 'vendeur') {
      navigate('/vendeur');
    } else {
      navigate('/client');
    }
  };

  const handleLogout = () => {
    authService.logout();
    setCurrentUser(null);
    navigate('/login');
  };

  const connected = !!currentUser;
  const hideFullHeader = location.pathname === '/login' || location.pathname === '/register' || location.pathname.startsWith('/admin');

  if (authLoading) {
    return <div className="ag-container">Chargement de l'authentification…</div>;
  }

  return (
    <div className="ag-container">
      {/* Header: minimal on auth pages */}
      {hideFullHeader ? (
        <header className="ag-sitebar">
          <div>
            <strong>Sènè Nogoya Conseil (SNC)</strong>
            <div className="ag-sitebar-subtitle">Semences, intrants et paiement mobile</div>
          </div>
          <nav className="ag-nav">
            <Link className="ag-nav-link" to="/privacy">Confidentialité</Link>
            {connected ? <button className="ag-nav-link" onClick={handleLogout}>Déconnexion</button> : null}
          </nav>
        </header>
      ) : (
        <header className="ag-sitebar">
          <div>
            <strong>Sènè Nogoya Conseil (SNC)</strong>
            <div className="ag-sitebar-subtitle">Semences, intrants et paiement mobile</div>
          </div>
          <nav className="ag-nav">
            {connected ? (
              currentUser.role === 'admin' ? (
                <>
                  <Link className="ag-nav-link" to="/admin">Tableau administrateur</Link>
                  <button className="ag-nav-link" onClick={() => navigate('/admin?panel=stock')}>Stock</button>
                  <button className="ag-nav-link" onClick={() => navigate('/admin?panel=users')}>Utilisateurs</button>
                  <button className="ag-nav-link" onClick={() => navigate('/admin?panel=users')}>Créer vendeur</button>
                  <button className="ag-nav-link" onClick={() => navigate('/admin?panel=add')}>Ajouter produit</button>
                  <button className="ag-nav-link" onClick={handleLogout}>Déconnexion</button>
                </>
              ) : currentUser.role === 'vendeur' ? (
                <>
                  <Link className="ag-nav-link" to="/vendeur">Tableau vendeur</Link>
                  <button className="ag-nav-link" onClick={handleLogout}>Déconnexion</button>
                </>
              ) : (
                <>
                  <Link className="ag-nav-link" to="/client">Accueil</Link>
                  <button className="ag-nav-link" onClick={() => navigate('/client/espace')}>Mon espace</button>
                  <Link className="ag-nav-link" to="/cart">Panier ({nbItems})</Link>
                  <button className="ag-nav-link" onClick={handleLogout}>Déconnexion</button>
                </>
              )
            ) : (
              <>
                <Link className="ag-nav-link" to="/login">Se connecter</Link>
                <Link className="ag-nav-link" to="/register">S'inscrire</Link>
              </>
            )}
            {!(location.pathname.startsWith('/admin')) && <Link className="ag-nav-link" to="/privacy">Confidentialité</Link>}
          </nav>
        </header>
      )}

      <main>
        <Routes>
          <Route
            path="/"
            element={
              connected ? (
                currentUser.role === 'admin' ? (
                  <Navigate to="/admin" replace />
                ) : currentUser.role === 'vendeur' ? (
                  <Navigate to="/vendeur" replace />
                ) : (
                  <Navigate to="/client" replace />
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/login"
            element={connected ? <Navigate to="/" replace /> : <LoginForm onSuccess={handleLoginSuccess} onSwitch={() => navigate('/register')} />}
          />
          <Route
            path="/register"
            element={connected ? <Navigate to="/" replace /> : <RegisterForm onSuccess={handleLoginSuccess} onSwitch={() => navigate('/login')} />}
          />
          <Route
            path="/admin"
            element={connected && currentUser.role === 'admin' ? <AdminDashboard /> : <Navigate to={connected ? '/' : '/login'} replace />}
          />
          <Route
            path="/vendeur"
            element={connected && currentUser.role === 'vendeur' ? <VendorDashboard /> : <Navigate to={connected ? '/' : '/login'} replace />}
          />
          <Route
            path="/client/espace"
            element={connected && currentUser.role === 'client' ? <Dashboard onAdd={ajouter} currentUser={currentUser} espaceMode /> : <Navigate to={connected ? '/' : '/login'} replace />}
          />
          <Route
            path="/client"
            element={connected && currentUser.role === 'client' ? <Dashboard onAdd={ajouter} currentUser={currentUser} /> : <Navigate to={connected ? '/' : '/login'} replace />}
          />
          <Route
            path="/produits/:id"
            element={<ProductDetail onAdd={ajouter} currentUser={currentUser} />}
          />
          {/* Route de debug non protégée pour visualiser l'interface vendeur localement */}
          <Route path="/_debug_vendor" element={<VendorDashboard />} />
          <Route
            path="/cart"
            element={<CartPage currentUser={currentUser} />}
          />
          <Route
            path="/privacy"
            element={<PrivacyPolicy />}
          />
          <Route
            path="/cmd/:numero"
            element={<OrderTrackingPage />}
          />
          <Route path="*" element={<Navigate to={connected ? '/' : '/login'} replace />} />
        </Routes>
      </main>
      <ChatWidget />
      <CookieConsentBanner />
    </div>
  );
}
